import React, { useEffect, useState } from 'react';
import { BRAND_NAME } from '../../theme/brand';
import {
    Box,
    VStack,
    Card,
    CardBody,
    Heading,
    Text,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Button,
} from '@chakra-ui/react';
import { FiClock, FiCopy, FiCheck } from 'react-icons/fi';
import {  } from "@chakra-ui/react";
import { fetchWrapper } from '../../helpers';

/**
 * Event ingestion endpoint shown in the SDK snippets. Read from the environment
 * so the documented URL matches the deployment the user is looking at, the same
 * way CustomEventAPI.jsx resolves it — the host is kept as the fallback so an
 * unset var still documents a working production endpoint rather than a bare path.
 */
const EVENT_ENDPOINT = `${process.env.REACT_APP_EVENT_API_URL || 'https://event.xnotify.ai'}/v2/save`;

const CodeCard = ({ code, section, copiedSection, copyToClipboard }) => (
    <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
        <Button
            position="absolute"
            top={3}
            right={3}
            size="sm"
            bg="whiteAlpha.200"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            leftIcon={copiedSection === section ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
            onClick={() => copyToClipboard(code, section)}
        >
            {copiedSection === section ? 'Copied!' : 'Copy'}
        </Button>
        <CardBody pt={10} pb={6} px={6}>
            <Box as="pre" fontFamily="mono" fontSize="sm" overflowX="auto" whiteSpace="pre">
                {code}
            </Box>
        </CardBody>
    </Card>
);

const IOSEventTracker = ({ copyToClipboard, copiedSection, list }) => {
    const swiftUIInitCode = `import SwiftUI
import EventTrackerSDK

@main
struct YourApp: App {
    init() {
        EventTracker.shared.initialize(
            eventKey: "${list?.event_key ?? 'YOUR_EVENT_KEY'}",
            apiEndpoint: "${EVENT_ENDPOINT}",
            debug: true
        )
    }
    var body: some Scene {
        WindowGroup {
            ContentView()
        }
    }
}`;

    const uiKitInitCode = `import UIKit
import EventTrackerSDK

@main
class AppDelegate: UIResponder, UIApplicationDelegate {
    func application(
        _ application: UIApplication,
        didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?
    ) -> Bool {
        EventTracker.shared.initialize(
            eventKey: "${list?.event_key ?? 'YOUR_EVENT_KEY'}",
            apiEndpoint: "${EVENT_ENDPOINT}",
            debug: true
        )
        return true
    }
}`;

    const trackPushCode = `private func trackPushIfNeeded(userInfo: [AnyHashable: Any]) {
    let messageId =
        userInfo["messageId"] as? String ??
        userInfo["mkey"] as? String ?? ""
    let rawFlag = userInfo["button_tracking"]
    let shouldTrack: Bool = {
        guard let rawFlag else { return true }
        if let value = rawFlag as? Bool { return value }
        if let value = rawFlag as? Int { return value == 1 }
        if let value = rawFlag as? String {
            let normalized = value.lowercased()
            return normalized == "true" || normalized == "1"
        }
        return true
    }()
    guard shouldTrack, !messageId.isEmpty else { return }
    EventTracker.shared.track("push_received", properties: [
        "message_id": messageId
    ])
}`;

    const trackScreenCode = `EventTracker.shared.track("page_view", properties: [
    "screen_name": "HomeScreen"
])`;

    const trackActionCode = `EventTracker.shared.track("button_clicked", properties: [
    "button_name": "continue"
])`;

    const identifyCode = `func onLoginSuccess(userPhone: String) {
    EventTracker.shared.identify(
        contactNumber: userPhone,
        traits: [
            "user_type": "customer"
        ]
    )
}`;

    const flushCode = `EventTracker.shared.flush()`;

    return (
        <VStack spacing={6} align="stretch">
            <Text color="gray.700">
                EventTrackerSDK enables event tracking for iOS applications, including screen/page views,
                user interaction events, and user identification. All events are sent to the {BRAND_NAME} Global Event API.
            </Text>

            {/* Step 1 - Adding the XCFramework */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">1. Adding the XCFramework</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <VStack spacing={3} align="stretch">
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Step 1 — Add Framework</Text>
                            <VStack spacing={1} align="stretch">
                                {[
                                    'Open your iOS project in Xcode.',
                                    'Drag EventTrackerSDK.xcframework into the Project Navigator.',
                                    'Select Copy items if needed.',
                                    'Select Add to your App Target.',
                                ].map((item, i) => (
                                    <Box key={i} display="flex" alignItems="flex-start" gap={2}>
                                        <Text color="blue.500" flexShrink={0}>{i + 1}.</Text>
                                        <Text color="gray.700" fontSize="sm">{item}</Text>
                                    </Box>
                                ))}
                            </VStack>
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Step 2 — Embed &amp; Sign</Text>
                            <VStack spacing={1} align="stretch">
                                {[
                                    'Select your App Target.',
                                    'Go to General → Frameworks, Libraries, and Embedded Content.',
                                    'Ensure EventTrackerSDK.xcframework is set to: Embed & Sign.',
                                ].map((item, i) => (
                                    <Box key={i} display="flex" alignItems="flex-start" gap={2}>
                                        <Text color="blue.500" flexShrink={0}>{i + 1}.</Text>
                                        <Text color="gray.700" fontSize="sm">{item}</Text>
                                    </Box>
                                ))}
                            </VStack>
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={1}>Backend Configuration</Text>
                            <Text color="gray.700" fontSize="sm">Production Endpoint: <code>{EVENT_ENDPOINT}</code></Text>
                            <Text color="gray.700" fontSize="sm" mt={1}>Authentication header: <code>X-Event-Key: YOUR_EVENT_KEY</code></Text>
                        </Box>
                    </VStack>
                </Box>
            </Box>

            {/* Step 2 - Initialize SDK */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">2. Initialize the SDK</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <Text color="gray.700" mb={3}>
                        Initialize once at app startup. Import the SDK first: <code>import EventTrackerSDK</code>
                    </Text>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>SwiftUI</Text>
                            <CodeCard code={swiftUIInitCode} section="ios-et-swiftui-init" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>UIKit (AppDelegate)</Text>
                            <CodeCard code={uiKitInitCode} section="ios-et-uikit-init" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                    </VStack>
                    <Box mt={4} p={4} bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="lg">
                        <Text fontSize="sm" color="gray.700">
                            Initialization must be called <strong>once only</strong>. Set <code>debug: true</code> only during development.
                        </Text>
                    </Box>
                </Box>
            </Box>

            {/* Step 3 - Tracking Events */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">3. Tracking Events</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Track Push Received</Text>
                            <Text color="gray.700" fontSize="sm" mb={2}>
                                Add this helper in <code>AppDelegate.swift</code> and call it inside <code>didReceiveRemoteNotification</code>:
                            </Text>
                            <CodeCard code={trackPushCode} section="ios-et-track-push" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Track a Screen View</Text>
                            <Text color="gray.700" fontSize="sm" mb={2}>Call when a screen becomes visible:</Text>
                            <CodeCard code={trackScreenCode} section="ios-et-track-screen" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Track a User Action</Text>
                            <CodeCard code={trackActionCode} section="ios-et-track-action" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                    </VStack>
                </Box>
            </Box>

            {/* Step 4 - Identify User */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">4. Identifying the User (After Login)</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <Text color="gray.700" mb={3}>
                        Calling <code>identify</code> links device activity to the authenticated user.
                        The phone number must come from your login/session system.
                    </Text>
                    <CodeCard code={identifyCode} section="ios-et-identify" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                </Box>
            </Box>

            {/* Step 5 - Flush */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">5. Flush (Optional)</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <Text color="gray.700" mb={3}>
                        Events are batched automatically. To send immediately:
                    </Text>
                    <CodeCard code={flushCode} section="ios-et-flush" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                    <Text color="gray.700" fontSize="sm" mt={3}>
                        Recommended before logout or during testing.
                    </Text>
                </Box>
            </Box>

            {/* Event Processing Behavior */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">6. Event Processing Behavior</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Card shadow="sm">
                    <CardBody p={6}>
                        <VStack spacing={2} align="stretch">
                            {[
                                'Events are queued in memory.',
                                'Automatically sent after 3 seconds of inactivity.',
                                'Also sent when 100 events are queued.',
                                'Failed events retry up to 3 times.',
                                'HTTP 200–299 and 202 responses are treated as success.',
                            ].map((item, i) => (
                                <Box key={i} display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="blue.500">•</Text>
                                    <Text color="gray.700">{item}</Text>
                                </Box>
                            ))}
                        </VStack>
                    </CardBody>
                </Card>
            </Box>

            {/* Verification */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">7. Verification</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <Text color="gray.700" mb={2}>
                        With <code>debug: true</code>, logs appear in Xcode:
                    </Text>
                    <CodeCard code={`[EventTracker] Queued: page_view | size=1\n[EventTracker] Sent: page_view | HTTP 202`} section="ios-et-verify-logs" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                    <Text color="gray.700" mt={3} fontWeight="semibold">Backend can verify events by:</Text>
                    <VStack spacing={1} align="stretch" mt={2}>
                        {['contact_no', 'distinct_id', 'event_name', 'timestamp'].map((f) => (
                            <Box key={f} display="flex" alignItems="center" gap={2}>
                                <Text color="blue.500">•</Text>
                                <Text color="gray.700" fontSize="sm"><code>{f}</code></Text>
                            </Box>
                        ))}
                    </VStack>
                </Box>
            </Box>

            {/* Best Practices */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">8. Best Practices</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Card shadow="sm">
                    <CardBody p={6}>
                        <VStack spacing={2} align="stretch">
                            {[
                                'Initialize SDK once at startup.',
                                'Call identify() immediately after successful login.',
                                'Track important business flows (login, OTP, payment, success events).',
                                'Avoid sending sensitive data in event properties.',
                            ].map((item, i) => (
                                <Box key={i} display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="blue.500">•</Text>
                                    <Text color="gray.700">{item}</Text>
                                </Box>
                            ))}
                        </VStack>
                    </CardBody>
                </Card>
            </Box>
        </VStack>
    );
};

const FlutterEventTracker = ({ copyToClipboard, copiedSection, list }) => {
    const [flutterTab, setFlutterTab] = useState(0);

    // ── Shared snippets ──────────────────────────────────────────────────────
    const pubspecCode = `dependencies:
  event_tracker_flutter: ^1.0.2`;

    const initCode = `import 'package:event_tracker_flutter/event_tracker_flutter.dart';

void main() async {
  WidgetsFlutterBinding.ensureInitialized();
  await EventTrackerFlutter.initialize(
    eventKey: '${list?.event_key ?? 'YOUR_EVENT_KEY'}',
    debug: false,
  );
  runApp(const MyApp());
}`;

    const trackCode = `await EventTrackerFlutter.track(
  eventName: 'button_clicked',
  properties: {
    'button_name': 'login',
  },
);`;

    const pageCode = `await EventTrackerFlutter.page(
  'Home Screen',
  properties: {
    'source': 'bottom_navigation',
  },
);`;

    const identifyCode = `await EventTrackerFlutter.identify(
  contactNumber: '923001234567',
  traits: {
    'name': 'John Doe',
    'email': 'john@example.com',
  },
);`;

    const identifyAnonCode = `await EventTrackerFlutter.identifyAnonymous(
  sessionId: 'guest_session_001',
  traits: {
    'user_type': 'guest',
  },
);`;

    // ── Android-specific snippets ────────────────────────────────────────────
    const settingsGradleKtsCode = `dependencyResolutionManagement {
  repositoriesMode.set(RepositoriesMode.PREFER_PROJECT)
  repositories {
    google()
    mavenCentral()
    // Add this line
    maven { url = uri("https://jitpack.io") }
  }
}`;

    const buildGradleKtsCode = `allprojects {
  repositories {
    google()
    mavenCentral()
    // Add this line
    maven { url = uri("https://jitpack.io") }
  }
}`;

    // ── iOS-specific snippets ────────────────────────────────────────────────
    const podfileCode = `platform :ios, '13.0'`;

    // ── Shared sections rendered inside each tab ─────────────────────────────
    const SharedSections = ({ prefix }) => (
        <VStack spacing={6} align="stretch">
            {/* Installation */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">1. Installation</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <Text color="gray.700" mb={4}>
                        Add the dependency in your <code>pubspec.yaml</code>:
                    </Text>
                    <CodeCard code={pubspecCode} section={`${prefix}-pubspec`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                    <Text color="gray.700" mt={4}>
                        Then run: <code>flutter pub get</code>
                    </Text>
                </Box>
            </Box>
        </VStack>
    );

    const TrackingSections = ({ prefix }) => (
        <VStack spacing={6} align="stretch">
            {/* Import & Initialize */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">Import &amp; Initialize SDK</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <Text color="gray.700" mb={4}>
                        Initialize once during app startup. Must be called only once.
                    </Text>
                    <CodeCard code={initCode} section={`${prefix}-init`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                    <Box mt={4} p={4} bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="lg">
                        <Text fontSize="sm" color="gray.700">
                            Set <code>debug: true</code> only during development. Avoid debug logs in production.
                        </Text>
                    </Box>
                </Box>
            </Box>

            {/* Track Events */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">Track Events</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Track Event</Text>
                            <CodeCard code={trackCode} section={`${prefix}-track`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Track Page / Screen</Text>
                            <CodeCard code={pageCode} section={`${prefix}-page`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Flush Events (send immediately)</Text>
                            <CodeCard code={`await EventTrackerFlutter.flush();`} section={`${prefix}-flush`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                            <Text fontSize="sm" color="gray.600" mt={2}>
                                Recommended before logout or during testing.
                            </Text>
                        </Box>
                    </VStack>
                </Box>
            </Box>

            {/* Identify User */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">Identify User (After Login)</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Box shadow="sm" className='box-style-card'>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text color="gray.700" mb={2}>Link device activity to the authenticated user:</Text>
                            <CodeCard code={identifyCode} section={`${prefix}-identify`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                        <Box>
                            <Text color="gray.700" fontWeight="semibold" mb={2}>Anonymous Identification</Text>
                            <CodeCard code={identifyAnonCode} section={`${prefix}-identify-anon`} copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                        </Box>
                    </VStack>
                </Box>
            </Box>

            {/* Best Practices */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">Notes &amp; Best Practices</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Card shadow="sm">
                    <CardBody p={6}>
                        <VStack spacing={2} align="stretch">
                            {[
                                'Initialize SDK once at app startup.',
                                'Call identify() immediately after successful login.',
                                'Set debug: true only during development.',
                                'Track important business flows (login, OTP, payment, success events).',
                                'Avoid sending sensitive data in event properties.',
                                'Ensure internet connectivity for event delivery.',
                            ].map((item, i) => (
                                <Box key={i} display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="blue.500">•</Text>
                                    <Text color="gray.700">{item}</Text>
                                </Box>
                            ))}
                        </VStack>
                    </CardBody>
                </Card>
            </Box>
        </VStack>
    );

    return (
        <VStack spacing={4} align="stretch">
            <Text color="gray.700">
                Cross-platform Flutter plugin for integrating the {BRAND_NAME} Event Tracker SDK on both Android and iOS.
                Supports event tracking, user identification, anonymous session tracking, page/screen tracking, and manual event flushing.
            </Text>

            <Tabs variant="enclosed" index={flutterTab} onChange={setFlutterTab}>
                <TabList
                    bg="#f1f5f9"
                    p="1"
                    borderRadius="full"
                    display="flex"
                    justifyContent="space-between"
                >
                    <Tab
                        flex="1"
                        _selected={{ bg: 'white', boxShadow: 'sm', fontWeight: '600' }}
                        borderRadius="full"
                        fontSize="sm"
                    >
                        Android
                    </Tab>
                    <Tab
                        flex="1"
                        _selected={{ bg: 'white', boxShadow: 'sm', fontWeight: '600' }}
                        borderRadius="full"
                        fontSize="sm"
                    >
                        iOS
                    </Tab>
                </TabList>

                <TabPanels>
                    {/* ── Android ── */}
                    <TabPanel px={0} pt={4}>
                        <VStack spacing={6} align="stretch">
                            <SharedSections prefix="and" />

                            {/* Android-specific setup */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">2. Android Additional Setup</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box shadow="sm" className='box-style-card'>
                                    <VStack spacing={4} align="stretch">
                                        <Box>
                                            <Text color="gray.700" fontWeight="semibold" mb={2}>
                                                Step 1 — Open <code>android/settings.gradle.kts</code>
                                            </Text>
                                            <CodeCard code={settingsGradleKtsCode} section="and-et-settings" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                                        </Box>
                                        <Box>
                                            <Text color="gray.700" fontWeight="semibold" mb={2}>
                                                Step 2 — Open <code>android/build.gradle.kts</code> (if repositories are declared there)
                                            </Text>
                                            <CodeCard code={buildGradleKtsCode} section="and-et-buildgradle" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                                        </Box>
                                    </VStack>
                                </Box>
                            </Box>

                            <TrackingSections prefix="and" />
                        </VStack>
                    </TabPanel>

                    {/* ── iOS ── */}
                    <TabPanel px={0} pt={4}>
                        <VStack spacing={6} align="stretch">
                            <SharedSections prefix="ios" />

                            {/* iOS-specific setup */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">2. iOS Additional Setup</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box shadow="sm" className='box-style-card'>
                                    <Text color="gray.700" mb={2}>
                                        Open <code>ios/Podfile</code> and ensure the minimum iOS version is set:
                                    </Text>
                                    <CodeCard code={podfileCode} section="ios-et-podfile" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                                    <Text color="gray.700" mt={4} mb={2}>Then run:</Text>
                                    <CodeCard code={`cd ios\npod install`} section="ios-et-pod-install" copiedSection={copiedSection} copyToClipboard={copyToClipboard} />
                                </Box>
                            </Box>

                            <TrackingSections prefix="ios" />
                        </VStack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </VStack>
    );
};

export const MobileSDK = () => {
    const [copiedSection, setCopiedSection] = useState(null);
    const [list, setList] = useState({ event_key: 'e3d53d55-fc48-49e4-985b-70afc015d5d7' });

    const loadList = async (id) => {

        const url = `${process.env.REACT_APP_API_URL}/customer/by-id`;
        const data = await fetchWrapper.get(url)

        setList(data.data)

    }

    useEffect(() => {
        loadList()
    }, [])

    const copyToClipboard = (text, section) => {
        const fallbackCopy = (text) => {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (err) {
                console.error('Fallback: Could not copy text', err);
            }
            document.body.removeChild(textArea);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(() => {
                fallbackCopy(text);
            });
        } else {
            fallbackCopy(text);
        }

        setCopiedSection(section);
        setTimeout(() => setCopiedSection(null), 2000);
    };

    // Code snippets
    const gradleSettingsCode = `dependencyResolutionManagement {
    repositoriesMode.set(RepositoriesMode.FAIL_ON_PROJECT_REPOS)
    repositories {
        google()
        mavenCentral()
        maven {
            url = uri("https://jitpack.io")
        }
    }
}`;

    const dependencyCode = `dependencies {
    implementation 'com.github.dilawarabbas1:event-tracking:global-1.0.2'
}`;

    const applicationClassCode = `import android.app.Application
import android.util.Log
import com.example.eventtrackersdk.EventTracker

class EventTrackingApp : Application() {

    override fun onCreate() {
        super.onCreate()

        // Initialize EventTracker SDK
        EventTracker.initialize(
            context = this,
            eventKey = ${list?.event_key},
            debug = true // Enable debug logs (recommended for debug builds)
        )

        Log.d("EventTracker", "EventTracker initialized successfully")
    }
}`;

    const manifestCode = `<manifest xmlns:android="http://schemas.android.com/apk/res/android"
    xmlns:tools="http://schemas.android.com/tools">

    <application
        android:name=".EventTrackingApp"
        tools:replace="android:name">

        <!-- Other activities, services, receivers -->

    </application>

</manifest>`;

    const mainActivityCode = `class MainActivity : AppCompatActivity() {

    private val tracker = EventTracker.getInstance()

    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        setContentView(R.layout.activity_main)

        val tvScreen = findViewById<TextView>(R.id.tvDeepLinkScreen)

        // Track screen view event
        tracker.track(
            "page_view",
            mapOf(
                "screen_name" to "MainActivity"
            )
        )

        // Track button click event
        tvScreen.setOnClickListener {
            tracker.track(
                "button_clicked",
                mapOf(
                    "button_name" to "back to login"
                )
            )
            gotoNextActivity(LoginActivity::class.java)
        }
    }
}`;

    return (
        <>
            <Card shadow="sm" bg="blue.50" borderColor="var(--brand-primary)" borderWidth={1} my={4}>
                <CardBody>
                    <Text fontSize={"2xl"} fontWeight={"semibold"}>Mobile App SDK</Text>
                    <Text color="gray.700">
                        Integrate the {BRAND_NAME} SDK into your mobile applications to track events seamlessly across Android, iOS, React Native, and Flutter.

                    </Text>
                </CardBody>
            </Card>
            <Tabs colorScheme="blue" variant="enclosed">
                <TabList
                    bg="#f1f5f9"
                    p="1"
                    borderRadius="full"
                    display="flex"
                    justifyContent="space-between"
                >
                    <Tab
                        flex="1"
                        _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                        borderRadius="full"
                        fontSize="sm"
                    >
                        Android
                    </Tab>
                    <Tab
                        flex="1"
                        _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                        borderRadius="full"
                        fontSize="sm"
                    >
                        iOS
                    </Tab>
                    <Tab
                        flex="1"
                        _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                        borderRadius="full"
                        fontSize="sm"
                    >
                        React Native
                    </Tab>
                    <Tab
                        flex="1"
                        _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                        borderRadius="full"
                        fontSize="sm"
                    >
                        Flutter
                    </Tab>
                </TabList>

                <TabPanels>
                    {/* Android Tab */}
                    <TabPanel p={0}>
                        <VStack spacing={6} align="stretch">
                            {/* Introduction */}
                            <Box>
                                <Text color="gray.700" mb={6}>
                                    This guide explains how to integrate the Event Tracker SDK into your Android application to track events such as screen views and user interactions.
                                </Text>
                            </Box>

                            {/* Step 1 */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >1. Update Gradle Settings (Project Level)</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box shadow="sm" className='box-style-card'>
                                    <Box >
                                        <Text color="gray.700" mb={4}>
                                            Add JitPack to your settings.gradle file so Gradle can resolve the SDK dependency.
                                        </Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'gradle-settings' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(gradleSettingsCode, 'gradle-settings')}
                                            >
                                                {copiedSection === 'gradle-settings' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <CardBody pt={10} pb={6} px={6}>
                                                <Box as="pre" fontFamily="mono" fontSize="sm" overflowX="auto" whiteSpace="pre">
                                                    {gradleSettingsCode}
                                                </Box>
                                            </CardBody>
                                        </Card>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Step 2 */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >2. Add SDK Dependency</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box shadow="sm" className='box-style-card'>
                                    <Box >
                                        <Text color="gray.700" mb={4}>
                                            In your app-level build.gradle (or build.gradle.kts), add the Event Tracker dependency:
                                        </Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'dependency' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(dependencyCode, 'dependency')}
                                            >
                                                {copiedSection === 'dependency' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <CardBody pt={10} pb={6} px={6}>
                                                <Box as="pre" fontFamily="mono" fontSize="sm" overflowX="auto" whiteSpace="pre">
                                                    {dependencyCode}
                                                </Box>
                                            </CardBody>
                                        </Card>
                                        <Text color="gray.700" mt={4}>
                                            Sync the project after adding the dependency.
                                        </Text>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Step 3 */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >3. Create EventTrackingApp Application Class</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box shadow="sm" className='box-style-card'>
                                    <Box >
                                        <Text color="gray.700" mb={4}>
                                            Create a custom Application class to initialize the Event Tracker SDK and handle background launches.
                                        </Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'app-class' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(applicationClassCode, 'app-class')}
                                            >
                                                {copiedSection === 'app-class' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <CardBody pt={10} pb={6} px={6}>
                                                <Box as="pre" fontFamily="mono" fontSize="sm" overflowX="auto" whiteSpace="pre">
                                                    {applicationClassCode}
                                                </Box>
                                            </CardBody>
                                        </Card>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Step 4 */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >4. Configure Application in AndroidManifest.xml</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box className='box-style-card' shadow="sm">
                                    <Box >
                                        <Text color="gray.700" mb={4}>
                                            Register the custom Application class in your AndroidManifest.xml.
                                        </Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'manifest' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(manifestCode, 'manifest')}
                                            >
                                                {copiedSection === 'manifest' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <CardBody pt={10} pb={6} px={6}>
                                                <Box as="pre" fontFamily="mono" fontSize="sm" overflowX="auto" whiteSpace="pre">
                                                    {manifestCode}
                                                </Box>
                                            </CardBody>
                                        </Card>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Step 5 */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >5. Track Events in an Activity</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Box className='box-style-card' shadow="sm">
                                    <Box >
                                        <Text color="gray.700" mb={4}>
                                            Use the EventTracker instance to track screen views and user interactions.
                                        </Text>
                                        <Text color="gray.700" mb={4} fontWeight="semibold">
                                            Example: MainActivity.kt
                                        </Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'main-activity' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(mainActivityCode, 'main-activity')}
                                            >
                                                {copiedSection === 'main-activity' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <CardBody pt={10} pb={6} px={6}>
                                                <Box as="pre" fontFamily="mono" fontSize="sm" overflowX="auto" whiteSpace="pre">
                                                    {mainActivityCode}
                                                </Box>
                                            </CardBody>
                                        </Card>
                                        <Box mt={4} p={4} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg">
                                            <Text fontSize="sm" color="gray.700" mb={2}>
                                                <Text as="strong">event_name:</Text> Name of the event (e.g., page_view, button_clicked)
                                            </Text>
                                            <Text fontSize="sm" color="gray.700">
                                                <Text as="strong">properties:</Text> Key-value data related to the event
                                            </Text>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Notes & Best Practices */}
                            <Box>
                                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Notes & Best Practices</Text>
                                <Box h="1px" bg="#EAEAEA" mb={4} />
                                <Card shadow="sm" bg="amber.50" borderColor="amber.200" borderWidth={1}>
                                    <CardBody p={6}>
                                        <VStack spacing={2} align="stretch">
                                            <Box display="flex" alignItems="flex-start" gap={2}>
                                                <Text color="amber.600">•</Text>
                                                <Text color="gray.700">Initialize EventTracker only once in the Application class.</Text>
                                            </Box>
                                            <Box display="flex" alignItems="flex-start" gap={2}>
                                                <Text color="amber.600">•</Text>
                                                <Text color="gray.700">Use debug = true in debug builds to enable logs.</Text>
                                            </Box>
                                            <Box display="flex" alignItems="flex-start" gap={2}>
                                                <Text color="amber.600">•</Text>
                                                <Text color="gray.700">Keep event names consistent and meaningful.</Text>
                                            </Box>
                                            <Box display="flex" alignItems="flex-start" gap={2}>
                                                <Text color="amber.600">•</Text>
                                                <Text color="gray.700">Avoid sending sensitive user data in event properties.</Text>
                                            </Box>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </Box>
                        </VStack>
                    </TabPanel>

                    {/* iOS Tab */}
                    <TabPanel p={0}>
                        <IOSEventTracker copyToClipboard={copyToClipboard} copiedSection={copiedSection} list={list} />
                    </TabPanel>

                    {/* React Native Tab - Coming Soon */}
                    <TabPanel p={0}>
                        <Card shadow="sm">
                            <CardBody p={12} textAlign="center">
                                <Box
                                    w="64px"
                                    h="64px"
                                    borderRadius="full"
                                    bg="blue.50"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    mb={4}
                                >
                                    <Box as={FiClock} size="32px" color="var(--brand-primary)" />
                                </Box>
                                <Heading size="xl" mb={2}>Coming Soon</Heading>
                                <Text color="gray.600">
                                    React Native SDK documentation is currently under development and will be available soon.
                                </Text>
                            </CardBody>
                        </Card>
                    </TabPanel>

                    {/* Flutter Tab */}
                    <TabPanel p={0}>
                        <FlutterEventTracker copyToClipboard={copyToClipboard} copiedSection={copiedSection} list={list} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </>

    );
};